<template>
  <v-container fluid class="pa-2">
    <v-alert
      type="info"
      dense
      text
      class="mb-3"
      icon="mdi-share-variant"
    >
      Shared Analysis
    </v-alert>

    <div v-if="loading" class="text-center my-12">
      <v-progress-circular indeterminate color="primary" size="64" />
      <p class="mt-4">Loading shared analysis...</p>
    </div>

    <div v-else-if="error" class="text-center my-12">
      <v-icon large color="error">mdi-alert-circle</v-icon>
      <p class="error--text mt-4">{{ error }}</p>
    </div>

    <div v-else-if="analysisData">
      <CoherenceAnalysisTab
        :interview-id="analysisId"
        :analysis-data="analysisData"
        :candidate-info="candidateOverlayInfo"
        :run-info="sharedRunInfo"
        :run-controls-read-only="true"
        :hide-share="true"
        :read-only="true"
        :hide-coherence="!featureFlags.coherence"
      />
    </div>
  </v-container>
</template>

<script>
import CoherenceAnalysisTab from '@/components/analysis/CoherenceAnalysisTab.vue';
import ShareService from '@/services/ShareService';
import { normalizeGestureDocumentNames } from '@/utils/gestureLabels';
import featureFlags from '@/config/featureFlags';

function buildEpisodeStartTimeMsMap(episodes) {
  const map = new Map();
  if (!Array.isArray(episodes)) return map;
  for (const ep of episodes) {
    if (!ep || typeof ep !== 'object') continue;
    const id = ep.episode_id ?? ep.episodeId;
    const start = ep.start_time_ms ?? ep.startTimeMs;
    if (id == null || start == null) continue;
    const n = Number(start);
    if (Number.isFinite(n)) map.set(String(id), n);
  }
  return map;
}

function resolveGestureAlertTimestampMs(alert, episodeStartById) {
  if (!alert || typeof alert !== 'object') return 0;
  const coerce = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  for (const key of ['timestamp_ms', 'timestampMs', 'start_time_ms', 'startTimeMs', 'time_ms', 'timeMs']) {
    const n = coerce(alert[key]);
    if (n !== null) return n;
  }
  const eid = alert.episode_id ?? alert.episodeId;
  if (eid != null && episodeStartById && episodeStartById.has(String(eid))) {
    return episodeStartById.get(String(eid));
  }
  return 0;
}

function normalizeGestureClassification(rawClassification, magnitude = null) {
  const value = String(rawClassification || '').trim().toLowerCase();
  if (['gesture', 'microgesture', 'alert', 'red'].includes(value)) return 'gesture';
  if (['sustained', 'warning', 'orange'].includes(value)) return 'sustained';
  const n = Number(magnitude);
  if (Number.isFinite(n)) return n >= 80 ? 'gesture' : 'sustained';
  return 'sustained';
}

function formatTimeFromMs(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n < 0) return '00:00:000';
  const totalSec = n / 1000;
  const mins = Math.floor(totalSec / 60);
  const secs = Math.floor(totalSec % 60);
  const millis = Math.floor(n % 1000);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}:${String(millis).padStart(3, '0')}`;
}

function buildGestureTableRows(gestures) {
  if (!gestures || !Array.isArray(gestures.alerts) || gestures.alerts.length === 0) return [];
  const episodeStarts = buildEpisodeStartTimeMsMap(gestures.episodes);
  return gestures.alerts.map((a) => {
    const tsMs = resolveGestureAlertTimestampMs(a, episodeStarts);
    const durRaw = a.duration_ms ?? a.durationMs;
    const durationMs = durRaw != null ? Number(durRaw) : 0;
    return {
      time: formatTimeFromMs(tsMs),
      is_speaking: false,
      alert: a.gesture_name || '-',
      direction: '-',
      head_pose: '-',
      lips: '-',
      brows: a.reason || '-',
      classification: normalizeGestureClassification(a.classification, a.magnitude),
      duration_seconds: Number.isFinite(durationMs) ? durationMs / 1000 : 0,
    };
  });
}

export default {
  name: 'SharedAnalysis',

  components: { CoherenceAnalysisTab },

  data() {
    return {
      loading: true,
      error: null,
      rawData: null,
    };
  },

  computed: {
    featureFlags() {
      return featureFlags;
    },

    token() {
      return this.$route.params.token;
    },

    analysisId() {
      return this.rawData?.metadata?.interviewInfo?.id || 0;
    },

    /** Run block frozen in the share token (metadata.run); null for legacy results. */
    sharedRunInfo() {
      const run = this.rawData?.metadata?.run;
      return run && typeof run === 'object' ? run : null;
    },

    analysisData() {
      if (!this.rawData) return null;
      const { metadata, transcription } = this.rawData;
      // Same ingestion rule as the store: stored documents may carry legacy ids.
      const gestures = normalizeGestureDocumentNames(this.rawData.gestures);

      const coherenceAlerts = gestures?.coherence_analysis?.coherence_alerts || [];
      const coherenceSemanticModes = gestures?.coherence_analysis?.semantic_modes || null;

      return {
        videoUrl: metadata.videoUrl,
        interviewInfo: metadata.interviewInfo,
        landmarksConfig: metadata.landmarksConfig,
        analysisStatus: metadata.analysisStatus,
        analysisProgress: metadata.analysisProgress,
        isManualAnalysis: true,
        curriculumSummary: metadata.curriculumSummary ?? null,
        gestureAnalysis: buildGestureTableRows(gestures),
        transcription: transcription?.transcription || [],
        coherenceAlerts,
        coherenceSemanticModes,
      };
    },

    candidateOverlayInfo() {
      const info = this.rawData?.metadata?.interviewInfo || {};
      const durationSecondsRaw = Number(info.analysisDurationSeconds ?? info.duration);
      const durationLabel =
        Number.isFinite(durationSecondsRaw) && durationSecondsRaw > 0
          ? this.formatDuration(durationSecondsRaw)
          : 'N/A';

      return {
        candidateName: info.candidateName || 'Shared Analysis',
        curriculumSummary: this.rawData?.metadata?.curriculumSummary || null,
        interviewDate: info.interviewDate ? new Date(info.interviewDate).toLocaleDateString() : 'N/A',
        videoDescription: info.description || null,
        analysisDurationLabel: durationLabel,
        status: info.status || null,
        headerTotalScoreLabel: null,
        headerTotalScorePercent: null,
        competencyScoreLabel: null,
        culturalScoreLabel: null,
        competencyScorePercent: null,
        culturalScorePercent: null,
        isManualAnalysis: true,
      };
    },
  },

  async mounted() {
    await this.loadSharedAnalysis();
  },

  methods: {
    async loadSharedAnalysis() {
      this.loading = true;
      this.error = null;
      try {
        const response = await ShareService.getSharedAnalysis(this.token);
        this.rawData = response.data.data;
      } catch (e) {
        const status = e?.response?.status;
        if (status === 404) {
          this.error = 'This share link is invalid or has expired.';
        } else {
          this.error = 'Failed to load the shared analysis. Please try again later.';
        }
      } finally {
        this.loading = false;
      }
    },

    formatDuration(seconds) {
      const m = Math.floor(seconds / 60);
      const s = Math.floor(seconds % 60);
      return `${m}m ${s.toString().padStart(2, '0')}s`;
    },
  },
};
</script>
